import cn from 'classnames';
import { RegistrationFormData } from 'features/registration/interface/registration';
import { FormikErrors, FormikTouched } from 'formik/dist/types';
import { ClipboardEvent, FC, useRef, useState } from 'react';
import InputError from 'shared/components/form/inputError';
import { disableInputAlphabets } from 'shared/util/utility';

import './styles.scss';

interface Props {
	additionalError?: string;
	setFieldValue: (field: string, val: any) => void;
	values: RegistrationFormData;
	name: string;
	errors: FormikErrors<{ [field: string]: any }>;
	touched: FormikTouched<{}>;
	onBlur: any;
	isFieldValueValid: (field: string, errors: any, touched: {}) => boolean;
	cyTestRef?: string;
	cyErrorRef?: string;
}

const numOfInputs = 4;

const SeparateNumericInputField: FC<Props> = ({
	additionalError,
	setFieldValue,
	values,
	name,
	onBlur,
	errors,
	touched,
	isFieldValueValid,
	cyTestRef,
	cyErrorRef,
}) => {
	const [smsCode, setSmsCode] = useState<string>('');
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const inputRefs = [...Array(numOfInputs)].map(() => useRef<HTMLInputElement | null>(null));

	const handleFocusChangeToNext = (index: number) => {
		inputRefs[index + 1]?.current?.focus();
	};

	const handleFocusChangeToPrev = (index: number) => {
		inputRefs[index - 1]?.current?.focus();
	};

	const handleValueChange = (index: number, newValue: string) => {
		if (newValue.length > 1) {
			newValue = newValue.charAt(newValue.length - 1);
		}

		const newSmsCode = smsCode.split('');
		newSmsCode[index] = newValue;
		setSmsCode(newSmsCode.join(''));

		// automatic focus movement to next input
		if (index < 3 && newValue !== '') {
			handleFocusChangeToNext(index);
		}

		setFieldValue('smsCode', newSmsCode.join(''));
	};

	const onFocus = (index: number) => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const target = inputRefs[index]!.current as HTMLInputElement;

		// to keep the focus on the first empty input when clicked on any other input after first empty input.
		const previousElement = target.previousElementSibling as HTMLInputElement | null;
		if (previousElement && previousElement.value === '') {
			return previousElement.focus();
		}

		// to select the value of the input to replace value
		target.setSelectionRange(0, target.value.length);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
		const target = inputRefs[index]?.current as HTMLInputElement;
		const keyPressed = e.key;

		if (keyPressed === 'ArrowRight' || keyPressed === 'ArrowDown') {
			e.preventDefault();
			return handleFocusChangeToNext(index);
		}

		if (keyPressed === 'ArrowLeft' || keyPressed === 'ArrowUp') {
			e.preventDefault();
			return handleFocusChangeToPrev(index);
		}

		// to select the value of the input to replace value
		target.setSelectionRange(0, target.value.length);

		if (e.key !== 'Backspace' || target.value !== '') {
			return;
		}

		if (index > 0) {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const previousElement = inputRefs[index - 1]!.current as HTMLInputElement;
			previousElement.focus();
		}
	};

	const value = (index: number) => {
		const valuesFromProps = values.smsCode;

		if (valuesFromProps) {
			return valuesFromProps.split('')[index];
		}

		return smsCode[index];
	};

	const handlePaste = (e: ClipboardEvent) => {
		e.preventDefault();
		const clipBoardContent = e.clipboardData.getData('text/plain');
		setFieldValue('smsCode', clipBoardContent);
	};

	const isError = isFieldValueValid(name, errors, touched);

	return (
		<div className="SeparateNumericInputField">
			<div className="SeparateNumericInputField__inputs">
				{[...Array(numOfInputs)].map((_, index) => {
					return (
						<input
							className={cn('SeparateNumericInputField__input', {
								'SeparateNumericInputField__input--error': isError || additionalError,
							})}
							ref={inputRefs[index]}
							autoComplete="one-time-code"
							onFocus={() => onFocus(index)}
							onChange={(e) => {
								const value = disableInputAlphabets(e);
								handleValueChange(index, value);
							}}
							onKeyDown={(e) => onKeyDown(e, index)}
							maxLength={1}
							key={index}
							value={value(index)}
							onPaste={handlePaste}
							required={true}
							onBlur={onBlur}
							inputMode="numeric"
							data-cy={cyTestRef}
						/>
					);
				})}
			</div>
			<InputError additionalError={additionalError} isError={isError} name={name} errors={errors} cyErrorRef={cyErrorRef} />
		</div>
	);
};

export default SeparateNumericInputField;
